import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import { reportPreferenceRecovery, reportPreferenceRecoveryMessage } from '../utils/reportPreferenceRecovery';
import { RestaurantAttendanceGrid as Grid, RestaurantAttendanceRow } from '../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../utils/apiError';
import { printHtmlInHiddenFrame } from '../utils/printDocument';
import { currentRestaurantMonth, isRestaurantMonth } from '../utils/restaurantMonth';
import { formatHours } from '../utils/restaurantPersonnelFormat';
import PersonalReportDeliveryPanel from './PersonalReportDeliveryPanel';
import './RestaurantMonthlyTimesheetPrint.css';
import RestaurantReportPreview from './RestaurantReportPreview';
import { restaurantReportFields } from '../utils/restaurantReportFields';

const fields = [
  ['position', 'Посада'],
  ['department', 'Підрозділ'],
  ['daily-hours', 'Фактичні години за днями'],
  ['weighted-hours', 'Зараховані години за днями'],
  ['interval-count', 'Кількість інтервалів'],
  ['notes', 'Примітки з інтервалів'],
] as const;
type Field = typeof fields[number][0];
type Preferences = { fields: Field[]; version: number };
const defaults: Field[] = fields.map(([key]) => key)
  .filter(key => restaurantReportFields['monthly-timesheet'].defaults.includes(key));
const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character] ?? character));
const hours = (value: number) => formatHours(value, 4, false);

function buildTimesheetHtml(month: string, days: string[], rows: RestaurantAttendanceRow[], selected: Field[]) {
  const enabled = new Set(selected);
  const dayColumns = (blockDays: string[]) => blockDays.map(day => {
    const date = escapeHtml(day.slice(-2));
    const labels = [
      enabled.has('daily-hours') ? `<th scope="col">${date}<small>факт</small></th>` : '',
      enabled.has('weighted-hours') ? `<th scope="col">${date}<small>зарах.</small></th>` : '',
    ].join('');
    return labels;
  }).join('');
  const summaryColumns = [
    enabled.has('daily-hours') ? '<th scope="col">Факт за місяць</th>' : '',
    enabled.has('weighted-hours') ? '<th scope="col">Зараховано за місяць</th>' : '',
    enabled.has('interval-count') ? '<th scope="col">Інтервали</th>' : '',
  ].join('');
  const employeeRows = (blockDays: string[], summary: boolean) => rows.map(employee => {
    const completedEntries = (entries: RestaurantAttendanceRow['cells'][number]['entries']) =>
      entries.filter(entry => entry.checkOutAt);
    const completedHours = (entries: RestaurantAttendanceRow['cells'][number]['entries']) =>
      completedEntries(entries).reduce((sum, entry) => sum + entry.hoursWorked, 0);
    const unfinishedHours = (entries: RestaurantAttendanceRow['cells'][number]['entries']) =>
      entries.filter(entry => !entry.checkOutAt).reduce((sum, entry) => sum + entry.hoursWorked, 0);
    const weightedHours = (entries: RestaurantAttendanceRow['cells'][number]['entries'], open: boolean) =>
      entries.filter(entry => Boolean(!entry.checkOutAt) === open)
        .reduce((sum, entry) => sum + entry.hoursWorked * entry.hourCoefficient, 0);
    const cells = blockDays.map(day => {
      const cell = employee.cells.find(item => item.workDate === day);
      const entries = cell?.entries ?? [];
      const openCount = entries.filter(entry => !entry.checkOutAt).length;
      const actualOpenHours = unfinishedHours(entries);
      const weightedOpenHours = weightedHours(entries, true);
      const unfinished = (amount: number) => openCount
        ? `<small class="unfinished">Незавершено:<br><span class="numeric-value">${escapeHtml(hours(amount))}</span> год${enabled.has('interval-count') ? `<br>${openCount} інтерв.` : ''}</small>`
        : '';
      return [
        enabled.has('daily-hours')
          ? `<td class="number"><span class="numeric-value">${escapeHtml(hours(completedHours(entries)))}</span>${unfinished(actualOpenHours)}</td>` : '',
        enabled.has('weighted-hours')
          ? `<td class="number"><span class="numeric-value">${escapeHtml(hours(weightedHours(entries, false)))}</span>${unfinished(weightedOpenHours)}</td>` : '',
      ].join('');
    }).join('');
    const intervalCount = employee.cells.reduce((sum, cell) => sum + cell.entries.length, 0);
    const openIntervalCount = employee.cells.reduce((sum, cell) =>
      sum + cell.entries.filter(entry => !entry.checkOutAt).length, 0);
    const monthlyCompletedHours = employee.cells.reduce((sum, cell) => sum + completedHours(cell.entries), 0);
    const monthlyUnfinishedHours = employee.cells.reduce((sum, cell) => sum + unfinishedHours(cell.entries), 0);
    const monthlyCompletedWeightedHours = employee.cells.reduce((sum, cell) =>
      sum + weightedHours(cell.entries, false), 0);
    const monthlyUnfinishedWeightedHours = employee.cells.reduce((sum, cell) =>
      sum + weightedHours(cell.entries, true), 0);
    return `<tr>
      <th scope="row">${escapeHtml(employee.fullName)}</th>
      ${enabled.has('position') ? `<td>${escapeHtml(employee.position || '—')}</td>` : ''}
      ${enabled.has('department') ? `<td>${escapeHtml(employee.departmentName || '—')}</td>` : ''}
      ${cells}
      ${summary && enabled.has('daily-hours') ? `<td class="number"><span class="numeric-value">${escapeHtml(hours(monthlyCompletedHours))}</span>
        ${openIntervalCount ? `<small class="unfinished">Незавершено: <span class="numeric-value">${escapeHtml(hours(monthlyUnfinishedHours))}</span> год</small>` : ''}</td>` : ''}
      ${summary && enabled.has('weighted-hours') ? `<td class="number"><span class="numeric-value">${escapeHtml(hours(monthlyCompletedWeightedHours))}</span>
        ${openIntervalCount ? `<small class="unfinished">Незавершено: <span class="numeric-value">${escapeHtml(hours(monthlyUnfinishedWeightedHours))}</span> год</small>` : ''}</td>` : ''}
      ${summary && enabled.has('interval-count') ? `<td class="number">${intervalCount}
        ${openIntervalCount ? `<small class="unfinished">Незавершених: ${openIntervalCount}</small>` : ''}</td>` : ''}
    </tr>`;
  }).join('');
  const monthLabel = new Intl.DateTimeFormat('uk-UA', { month: 'long', year: 'numeric' })
    .format(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1));
  const identity = '<th scope="col">Працівник</th>' +
    (enabled.has('position') ? '<th scope="col">Посада</th>' : '') +
    (enabled.has('department') ? '<th scope="col">Підрозділ</th>' : '');
  const table = (blockDays: string[], summary: boolean, caption: string) => `<section>
    <table><caption>${escapeHtml(caption)}</caption><thead><tr>${identity}${dayColumns(blockDays)}${summary ? summaryColumns : ''}</tr></thead>
      <tbody>${employeeRows(blockDays, summary) || `<tr><td colspan="${1 + Number(enabled.has('position')) +
        Number(enabled.has('department')) + blockDays.length *
        (Number(enabled.has('daily-hours')) + Number(enabled.has('weighted-hours'))) +
        (summary ? Number(enabled.has('daily-hours')) + Number(enabled.has('weighted-hours')) +
        Number(enabled.has('interval-count')) : 0)}">За цей місяць немає працевлаштувань.</td></tr>`}</tbody>
    </table></section>`;
  const blocks: string[] = [];
  if (enabled.has('daily-hours') || enabled.has('weighted-hours'))
    for (let offset = 0; offset < days.length; offset += 7) {
      const block = days.slice(offset, offset + 7);
      blocks.push(table(block, false, `Дні ${block[0]} — ${block[block.length - 1]} · ${month}`));
    }
  const noteRows = enabled.has('notes') ? rows.flatMap(employee => employee.cells.flatMap(cell =>
    cell.entries.filter(entry => entry.comment.trim()).map(entry => `<tr>
      <th scope="row">${escapeHtml(employee.fullName)}</th><td>${escapeHtml(cell.workDate)}</td>
      <td class="notes">${escapeHtml(entry.comment.trim())}</td></tr>`))).join('') : '';
  return `<!doctype html><html lang="uk"><head><meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>${escapeHtml(`Місячний табель — ${month}`)}</title>
    <style>
      @page { size: A3 landscape; margin: 8mm; }
      * { box-sizing: border-box; } body { color:#172b3a; font:8pt/1.35 Arial,sans-serif; }
      h1 { font-size:18pt; margin:0 0 4px; } p { margin:4px 0 12px; }
      table { width:100%; border-collapse:collapse; table-layout:fixed; margin-bottom:16px; }
      caption { text-align:left; font-size:12pt; font-weight:bold; padding:6px 0; }
      th,td { border:1px solid #aebdc4; padding:3px; vertical-align:top; overflow-wrap:anywhere; }
      thead th { background:#e7eef1; text-align:center; } thead small { display:block; font-size:6pt; }
      tbody th { text-align:left; } .number { text-align:right; font-variant-numeric:tabular-nums; }
      .numeric-value { white-space:nowrap; overflow-wrap:normal; word-break:normal; }
      .notes { white-space:pre-wrap; }
      small.unfinished { display:block; margin-top:2px; color:#7a3d00; text-align:left; }
      @media print { thead { display:table-header-group; } tr { break-inside:avoid; } section + section { break-before:page; } }
    </style></head><body>
      <h1>Місячний табель працівників</h1>
      <p>Період: ${escapeHtml(monthLabel)} · Працівників: ${rows.length} · Підсумки охоплюють завершені інтервали; незавершені години позначені окремо. Відсутність запису не є позначкою прогулу.</p>
      ${blocks.join('')}
      ${table([], true, `Підсумок за весь місяць · ${month}`)}
      ${enabled.has('notes') ? `<section><table class="note-table"><caption>Примітки за місяць · ${escapeHtml(month)}</caption>
        <thead><tr><th scope="col">Працівник</th><th scope="col">Дата</th><th scope="col">Примітка</th></tr></thead>
        <tbody>${noteRows || '<tr><td colspan="3">Приміток за місяць немає.</td></tr>'}</tbody>
      </table></section>` : ''}
    </body></html>`;
}

const RestaurantMonthlyTimesheetPrint: React.FC<{
  operatingGroupId?: number;
  initialMonth?: string;
}> = ({
  operatingGroupId, initialMonth,
}) => {
  const [month, setMonth] = useState(() => isRestaurantMonth(initialMonth) ? initialMonth : currentRestaurantMonth());
  const [preferences, setPreferences] = useState<Preferences>({ fields: defaults, version: 0 });
  const [ready, setReady] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showReportDelivery, setShowReportDelivery] = useState(false);
  const [preview, setPreview] = useState<{ html: string; context: string; generation: number } | null>(null);
  const request = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const context = JSON.stringify([month, operatingGroupId ?? null, preferences.fields]);
  const previewHtml = preview?.context === context && preview.generation === generation.current
    ? preview.html : null;
  const reportSection = useRef<HTMLElement>(null);

  useEffect(() => {
    if (window.location.hash !== '#restaurant-monthly-reports') return;
    const frame = window.requestAnimationFrame(() => {
      reportSection.current?.focus({ preventScroll: true });
      reportSection.current?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useLayoutEffect(() => {
    generation.current += 1;
    request.current?.abort();
    request.current = null;
    setBusy(false);
    setSaving(false);
    setNotice('');
    setError('');
    setPreview(null);
    return () => {
      ++generation.current;
      request.current?.abort();
      request.current = null;
    };
  }, [context]);

  useEffect(() => {
    const controller = new AbortController();
    setReady(false);
    setRecovering(false);
    void api.get<Preferences>('/restaurant-personnel/report-preferences/monthly-timesheet', {
      signal: controller.signal,
    }).then(response => {
      if (controller.signal.aborted) return;
      setPreferences(response.data);
      setReady(true);
    }).catch(failure => {
      if (!controller.signal.aborted) {
        const recovery = reportPreferenceRecovery(failure, 'monthly-timesheet', 'print');
        if (recovery) {
          setPreferences({ fields: [], version: recovery.version });
          setRecovering(true);
          setReady(true);
          return;
        }
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити збережені поля табеля.'));
      }
    });
    return () => controller.abort();
  }, [operatingGroupId]);

  const savePreferences = async (): Promise<boolean> => {
    if (!ready || saving) return false;
    const saveGeneration = generation.current;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.put<Preferences>(
        '/restaurant-personnel/report-preferences/monthly-timesheet', preferences);
      if (generation.current !== saveGeneration) return false;
      setPreferences(response.data);
      setRecovering(false);
      setNotice('Вибір полів збережено для вашого облікового запису на всіх пристроях.');
      return true;
    } catch (failure) {
      if (generation.current === saveGeneration)
        setError(extractApiErrorMessage(failure, 'Не вдалося зберегти поля табеля. Оновіть їх після конфлікту.'));
    } finally {
      if (generation.current === saveGeneration) setSaving(false);
    }
    return false;
  };

  const print = async (previewOnly = false) => {
    if (!ready || recovering || busy) return;
    const [year, selectedMonth] = month.split('-').map(Number);
    request.current?.abort();
    const controller = new AbortController();
    const printGeneration = generation.current;
    request.current = controller;
    const valid = () => !controller.signal.aborted && request.current === controller &&
      generation.current === printGeneration;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const employees: RestaurantAttendanceRow[] = [];
      let total = 0;
      let days: string[] = [];
      let snapshotVersion = '';
      const employmentIds = new Set<number>();
      const pageSizes = [100, 25, 5, 1];
      let sizeIndex = 0;
      for (let page = 1; ; page++) {
        let response;
        try {
          response = await api.get<Grid>('/restaurant-personnel/attendance-grid', {
            signal: controller.signal,
            params: { year, month: selectedMonth, page, pageSize: pageSizes[sizeIndex], reportSnapshot: true },
          });
        } catch (failure) {
          if (!controller.signal.aborted && axios.isAxiosError<{ code?: string }>(failure) &&
              failure.response?.status === 422 && failure.response.data?.code === 'attendance_grid_interval_limit' &&
              sizeIndex < pageSizes.length - 1) {
            sizeIndex++;
            employees.length = 0;
            employmentIds.clear();
            page = 0;
            continue;
          }
          throw failure;
        }
        if (!valid()) return;
        if (!response.data.snapshotVersion || response.data.employees.page !== page)
          throw new Error('Не вдалося підтвердити узгодженість місячного табеля. Оновіть сторінку та повторіть друк.');
        if (page === 1) {
          total = response.data.employees.total;
          days = response.data.days;
          snapshotVersion = response.data.snapshotVersion;
        } else if (response.data.employees.total !== total ||
            response.data.days.join('|') !== days.join('|') ||
            response.data.snapshotVersion !== snapshotVersion) {
          throw new Error('Місячний табель змінився під час підготовки. Повторіть друк.');
        }
        const pageRows = response.data.employees.items;
        for (const employee of pageRows) {
          if (employmentIds.has(employee.employmentId))
            throw new Error('Склад місячного табеля змінився під час підготовки. Повторіть друк.');
          employmentIds.add(employee.employmentId);
        }
        employees.push(...pageRows);
        if (employees.length > total)
          throw new Error('Склад місячного табеля змінився під час підготовки. Повторіть друк.');
        if (employees.length >= total) break;
        if (pageRows.length === 0)
          throw new Error('Не вдалося завантажити всі сторінки місячного табеля.');
      }
      if (!valid()) return;
      const html = buildTimesheetHtml(month, days, employees, preferences.fields);
      if (previewOnly) {
        setPreview({ html, context, generation: printGeneration });
        return;
      }
      setNotice(`Підготовлено повний табель за місяць: ${employees.length} працевлаштувань.`);
      printHtmlInHiddenFrame(html, {
        signal: controller.signal,
        onError: () => {
          if (!valid()) return;
          setNotice('');
          setError('Не вдалося підготувати табель до друку. Повторіть друк або збереження PDF.');
        },
      });
    } catch (failure) {
      if (valid())
        setError(extractApiErrorMessage(failure, 'Не вдалося завантажити повний місячний табель.'));
    } finally {
      if (valid()) setBusy(false);
    }
  };

  return <section ref={reportSection} id="restaurant-monthly-reports" tabIndex={-1} className="restaurant-timesheet-print" aria-label="Друк повного місячного табеля">
    <h2>Друк місячного табеля</h2>
    <p>Звіт охоплює всі працевлаштування за місяць. Поля зберігаються у вашому обліковому записі.</p>
    {recovering && <p role="alert">{reportPreferenceRecoveryMessage}</p>}
    <div className="restaurant-timesheet-print__fields">
      {fields.map(([key, label]) => <label key={key}>
        <input type="checkbox" checked={preferences.fields.includes(key)} disabled={!ready || saving || busy}
          onChange={event => setPreferences(current => ({
            ...current,
            fields: event.target.checked
              ? [...current.fields, key]
              : current.fields.filter(field => field !== key),
          }))} />
        {label}
      </label>)}
    </div>
    <div className="restaurant-timesheet-print__actions">
      <label>Місяць<input type="month" min="2000-01" max="2200-12" value={month} disabled={busy}
        onChange={event => {
          if (/^\d{4}-(0[1-9]|1[0-2])$/.test(event.target.value)) setMonth(event.target.value);
        }} /></label>
      <button type="button" disabled={!ready || saving || busy} onClick={() => void savePreferences()}>
        Зберегти поля
      </button>
      <button type="button" disabled={!ready || saving || busy}
        onClick={() => setPreferences(current => ({ ...current, fields: defaults }))}>Типові поля</button>
      <button type="button" disabled={!ready || recovering || saving || busy} onClick={() => void print(true)}>Попередній перегляд</button>
      <button type="button" disabled={!ready || recovering || saving || busy} onClick={() => void print()}>
        {busy ? 'Завантаження всіх сторінок…' : 'Друк / PDF'}
      </button>
    </div>
    <button type="button" disabled={!ready || recovering || saving || busy}
      onClick={() => setShowReportDelivery(value => !value)}>
      {showReportDelivery ? 'Приховати приватну відправку' : 'Приватна відправка'}
    </button>
    {showReportDelivery && <PersonalReportDeliveryPanel
      reportBlocked={!ready || recovering}
      template="monthly-timesheet"
      year={Number(month.slice(0, 4))}
      month={Number(month.slice(5, 7))}
      scopeKey={String(operatingGroupId ?? '')}
    />}
    {error && <p role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    <RestaurantReportPreview html={previewHtml} title="Перегляд місячного табеля" onClose={() => setPreview(null)} />
  </section>;
};

export default RestaurantMonthlyTimesheetPrint;
